// ============================================================
// format.js
// Display formatting helpers. Centralised so date strings and
// numbers render consistently across the admin SWA.
//
// All inputs assumed UTC ISO-8601 strings from the backend. The
// admin SWA operators are UK-based A2Z IT staff; en-GB locale
// renders dates as "13 May 2026". No timezone conversion in v1.
// ============================================================

/**
 * Format an ISO-8601 string as a short date: "13 May 2026".
 * Returns '' for null/undefined/invalid input.
 */
export function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/**
 * Format an ISO-8601 string as a short date+time:
 * "13 May 2026 at 14:32 UTC". 24-hour clock to avoid
 * AM/PM ambiguity in audit logs.
 */
export function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // BOTH halves in UTC. The date half originally rendered in the
  // browser's LOCAL zone while the time was forced UTC - so any
  // 23:00-00:00 UTC timestamp under BST displayed a day AHEAD of the
  // labelled-UTC time ("21 Aug at 23:11 UTC" for 2026-08-20T23:11Z).
  // Found live 2026-08-21 on the deletion-request queue.
  const date = d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC',
  });
  return `${date} at ${time} UTC`;
}

/**
 * Relative-future for AccessExpiresAt etc.: "in 14 days", "in 3h",
 * "Expired" if past. Empty input → ''.
 */
export function formatRelativeFuture(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = d.getTime() - Date.now();
  if (diffMs < 0) return 'Expired';
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return `in ${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
}

/**
 * Capitalise the first letter; passes non-strings through.
 */
export function capitalise(s) {
  if (typeof s !== 'string' || s.length === 0) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
